import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import { addMinutes, format, isAfter } from 'date-fns';

import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';

import ArgonBox from 'component/Argon/ArgonBox';
import ArgonTypography from 'component/Argon/ArgonTypography';

import DashboardLayout from 'layout/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'component/Navbars/DashboardNavbar';

import { useOrder } from 'api/getOrder';
import { useQrOrder } from 'api/reQrOrder';

import useCart from 'hook/useCart';

import ArgonButton from 'component/Argon/ArgonButton';
import axios from 'axios';

const minuteTimeout = 10;
function OrderPayment() {
  const { invoiceId } = useParams();
  const navigate = useNavigate();
  const { removeAll, items } = useCart();
  const [isAlive, setAlive] = useState(undefined);
  const { mutateAsync: reQrOrder } = useQrOrder();
  const { data, isLoading, isSuccess, isError, refetch } = useOrder(invoiceId);

  useEffect(() => {
    if (isSuccess && data?.qrURL) {
      if (items.length) {
        removeAll();
      }
      if (data?.isPaid) {
        handleSuccess();
      }
    } else if (isError) {
      navigate('/notfound');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, isSuccess, isError, data]);

  useEffect(() => {
    let interval = null;

    if (data?.qrTime) {
      interval = setInterval(() => {
        const curDate = new Date();
        // console.log(new Date(data.qrTime).toString());
        if (isAfter(addMinutes(new Date(data.qrTime), minuteTimeout), curDate)) {
          setAlive(true);
        } else {
          setAlive(false);
        }
      }, 1000);
      if (isAfter(addMinutes(new Date(data.qrTime), minuteTimeout), new Date())) {
        Swal.fire({
          title: `QRCODE มีอายุ ${minuteTimeout} นาที`,
          text: '',
          icon: 'info',
          iconColor: 'red',
          confirmButtonText: 'ปิด',
        });
      }
    }
    return () => {
      clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.qrTime]);

  function handleSuccess() {
    const newSwal = Swal.mixin({
      customClass: {
        confirmButton: 'button button-success',
      },
      buttonsStyling: false,
      allowOutsideClick: false,
      allowEscapeKey: false,
    });

    newSwal
      .fire({
        title: 'การชำระเงินสำเร็จ',
        text: '',
        icon: 'success',
        confirmButtonText: 'ต่อไป',
      })
      .then((result) => {
        if (result.isConfirmed) {
          navigate('/order/' + invoiceId);
        }
      });
  }

  async function handleReQr() {
    Swal.fire({
      title: 'กำลังดำเนินการ',
      timerProgressBar: true,
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
        reQrOrder(invoiceId)
          .then(() => {
            refetch().finally(() => {
              Swal.close();
            });
          })
          .catch((err) => {
            let errMsg = handleAxios(err);
            Swal.fire('ล้มเหลว', `${errMsg !== '' ? errMsg : err?.response}`, 'error');
          });
      },
    });
  }

  function handleAxios(err) {
    let errMsg;
    if (axios.isAxiosError(err)) {
      if (err.response?.data) {
        errMsg = err.response.data;
      }
    }
    return errMsg;
  }

  if (isLoading) {
    return (
      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={true}>
        <CircularProgress color="inherit" />
      </Backdrop>
    );
  }

  return (
    <DashboardLayout bgColor="success">
      <DashboardNavbar />
      {isSuccess && data?.qrURL && (
        <ArgonBox py={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={12}>
              <ArgonBox>
                <Card>
                  <ArgonBox display="flex" justifyContent="space-between" alignItems="flex-start" p={3}>
                    <ArgonBox lineHeight={1}>
                      <ArgonTypography variant="h5" fontWeight="medium">
                        กรุณาชำระเงิน
                      </ArgonTypography>
                      <ArgonTypography variant="button" fontWeight="regular" color="text">
                        ชำระด้วย QR Payment เพื่อดำเนินการซื้อกรมธรรม์
                      </ArgonTypography>
                    </ArgonBox>
                  </ArgonBox>
                  {isAlive ? (
                    <>
                      <Stack mb={1} direction="row" justifyContent="center" alignItems="center" spacing={2} paddingX={1}>
                        <img
                          src={'https://tkr.sgp1.digitaloceanspaces.com/' + data.qrURL}
                          alt="qr-code"
                          style={{
                            width: '100%',
                            minWidth: 250,
                            maxWidth: 400,
                            height: 'auto',
                          }}
                        />
                      </Stack>
                      <Stack mb={6} alignItems="center">
                        <ArgonTypography variant="h5">บริษัท ทีเคอาร์โบรกเกอร์เรจ</ArgonTypography>
                        <ArgonTypography variant="h3">จำนวน {Number(data.amount).toFixed(2)} บาท</ArgonTypography>
                        <ArgonTypography variant="h5">
                          กรุณาชำระเงินก่อน {format(addMinutes(new Date(data?.qrTime), minuteTimeout), 'dd/MM/yyyy hh:mm:ss')} น.
                        </ArgonTypography>
                      </Stack>
                    </>
                  ) : (
                    <Stack mb={6} alignItems="center" spacing={4}>
                      <ArgonTypography variant="h5">หมดเวลาชำระเงิน</ArgonTypography>
                      <ArgonButton variant="gradient" color="primary" size="large" onClick={handleReQr}>
                        ชำระเงินอีกครั้ง
                      </ArgonButton>
                    </Stack>
                  )}
                </Card>
              </ArgonBox>
            </Grid>
          </Grid>
        </ArgonBox>
      )}
    </DashboardLayout>
  );
}

export default OrderPayment;
